'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import type { AuthTokens } from '../lib/cookieUtils';
import { clearTokens, getBearerToken, getRefreshToken, getSecondsUntilExpiry, hasMb3SessionCookie, setTokens } from '../lib/cookieUtils';
import { ApiError, bffLogout, fetchSession, initiateLogin, MB2_ACCOUNT_EXISTS_ERROR_CODE, refreshAccessToken, refreshSessionCookies, type SessionPayload, type VerifyResult, verifyOtp } from '../lib/authApi';
import { setupFetchInterceptor } from '../lib/fetchInterceptor';
import { acquireRefreshLock } from '../lib/refreshLock';
import { REFRESH_THRESHOLD_SECONDS } from '../lib/constants';
const REJECTED_REFRESH_STATUSES = new Set([400, 401, 403]);
export type AuthStatus = 'loading' | 'unauthenticated' | 'sending' | 'awaiting-otp' | 'verifying' | 'authenticated' | 'registration-required' | 'mb2-migration-required' | 'logging-out' | 'expired';

// Auth has finished checking and nobody is signed in. `loading` is not signed out: the
// session check has not answered yet.
export function isSignedOutStatus(status: AuthStatus): boolean {
  return status === 'unauthenticated' || status === 'expired';
}

// Stable states stored in React state.
// Transient states (sending, verifying, logging-out) are derived from SWR isMutating flags.
type SessionStatus = Extract<AuthStatus, 'loading' | 'unauthenticated' | 'awaiting-otp' | 'authenticated' | 'registration-required' | 'mb2-migration-required' | 'expired'>;
export type SessionUser = SessionPayload['user'];
export type AuthContextType = {
  status: AuthStatus;
  user: SessionUser | null;
  isAccountComplete: boolean;
  error: string | null;
  initiateLogin: (email: string) => Promise<void>;
  verifyOtp: (email: string, otp: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  resetLoginFlow: () => void;
  // The mount-time session came from /auth/me renewing a lapsed one, after the
  // page's first requests had already gone out as a guest.
  sessionRenewed: boolean;
};
const AuthContext = createContext<AuthContextType | null>(null);
type AuthProviderProps = PropsWithChildren<{
  initialUser?: SessionUser | null;
  onSessionExpired?: () => void;
}>;
export function AuthProvider(t0) {
  const $ = _c(45);
  const {
    children,
    onSessionExpired,
    initialUser: t1
  } = t0;
  const initialUser = t1 === undefined ? null : t1;
  const initialComplete = !!initialUser?.accountComplete;
  const [sessionStatus, setSessionStatus] = useState(initialUser ? initialComplete ? "authenticated" : "unauthenticated" : "loading");
  const [user, setUser] = useState(initialComplete ? initialUser : null);
  const sessionFetchAbortedRef = useRef(false);
  const [sessionRenewed, setSessionRenewed] = useState(false);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      onSuccess: () => setSessionStatus("awaiting-otp"),
      onError: err => {
        if (err.message === MB2_ACCOUNT_EXISTS_ERROR_CODE) {
          setSessionStatus("mb2-migration-required");
          return;
        }
        setSessionStatus("unauthenticated");
      },
      throwOnError: false
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    trigger: triggerInitiateLogin,
    isMutating: isSending,
    error: initiateError,
    reset: resetInitiateLogin
  } = useSWRMutation("auth/initiate", _temp, t2);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      onSuccess: async verifyResult_0 => {
        if (verifyResult_0.sourceSystem && verifyResult_0.sourceSystem !== "magento") {
          clearTokens();
          setUser(null);
          setSessionStatus("mb2-migration-required");
          return;
        }
        try {
          const session = await fetchSession();
          if (!session) {
            setSessionStatus("awaiting-otp");
            return;
          }
          if (!session.user.accountComplete) {
            clearTokens();
            setUser(null);
            setSessionStatus("registration-required");
            return;
          }
          setUser(session.user);
          setSessionStatus("authenticated");
        } catch {
          setSessionStatus("awaiting-otp");
        }
      },
      onError: () => setSessionStatus("awaiting-otp"),
      throwOnError: false
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const {
    trigger: triggerVerifyOtp,
    isMutating: isVerifying,
    error: verifyError,
    reset: resetVerifyOtp
  } = useSWRMutation("auth/verify", _temp2, t3);
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      onSuccess: () => {
        sessionFetchAbortedRef.current = true;
        setUser(null);
        setSessionStatus("unauthenticated");
      },
      throwOnError: false
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const {
    trigger: triggerLogout,
    isMutating: isLoggingOut,
    error: logoutError,
    reset: resetLogout
  } = useSWRMutation("auth/logout", _temp3, t4);
  let t5;
  bb0: {
    if (isSending) {
      t5 = "sending";
      break bb0;
    }
    if (isVerifying) {
      t5 = "verifying";
      break bb0;
    }
    if (isLoggingOut) {
      t5 = "logging-out";
      break bb0;
    }
    t5 = sessionStatus;
  }
  const status = t5;
  let t6;
  bb1: {
    if (initiateError instanceof Error) {
      t6 = initiateError.message;
      break bb1;
    }
    if (verifyError instanceof Error) {
      t6 = verifyError.message;
      break bb1;
    }
    if (logoutError instanceof Error) {
      t6 = logoutError.message;
      break bb1;
    }
    t6 = null;
  }
  const error = t6;
  const refreshSuccessHandler = _temp4;
  let t7;
  if ($[3] !== onSessionExpired) {
    t7 = () => {
      clearTokens();
      setUser(null);
      setSessionStatus("expired");
      onSessionExpired?.();
    };
    $[3] = onSessionExpired;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const refreshFailureHandler = t7;
  let t8;
  if ($[5] !== initialUser) {
    t8 = () => {
      if (initialUser) {
        return;
      }
      let cancelled = false;
      fetchSession().then(session_0 => {
        if (cancelled || sessionFetchAbortedRef.current) {
          return;
        }
        if (session_0?.renewed) {
          setSessionRenewed(true);
        }
        if (session_0 && session_0.user.accountComplete) {
          setUser(session_0.user);
          setSessionStatus("authenticated");
          return;
        }
        setSessionStatus("unauthenticated");
      }).catch(() => {
        if (!cancelled) {
          setSessionStatus("unauthenticated");
        }
      });
      return () => {
        cancelled = true;
      };
    };
    $[5] = initialUser;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [];
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[8] !== refreshFailureHandler || $[9] !== status) {
    t10 = () => {
      if (status !== "authenticated") {
        return;
      }
      const cleanup = setupFetchInterceptor({
        getAccessToken: _temp5,
        getRefreshToken: _temp6,
        onRefreshSuccess: refreshSuccessHandler,
        onRefreshFailure: refreshFailureHandler
      });
      return cleanup;
    };
    t11 = [status, refreshSuccessHandler, refreshFailureHandler];
    $[8] = refreshFailureHandler;
    $[9] = status;
    $[10] = t10;
    $[11] = t11;
  } else {
    t10 = $[10];
    t11 = $[11];
  }
  useEffect(t10, t11);
  let t12;
  if ($[12] !== refreshFailureHandler) {
    t12 = async () => {
      const accessToken = getBearerToken();
      const secondsRemaining = accessToken ? getSecondsUntilExpiry(accessToken) : 0;
      if (secondsRemaining > REFRESH_THRESHOLD_SECONDS) {
        return null;
      }
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        if (hasMb3SessionCookie()) {
          return null;
        }
        ;
        try {
          await refreshSessionCookies();
        } catch (t13) {
          const err_0 = t13;
          if (err_0 instanceof ApiError && REJECTED_REFRESH_STATUSES.has(err_0.status)) {
            refreshFailureHandler();
          }
        }
        return null;
      }
      try {
        const newToken = await acquireRefreshLock(async () => {
          const tokens = await refreshAccessToken(refreshToken);
          setTokens(tokens);
          refreshSuccessHandler(tokens);
          return tokens.access_token;
        });
        return {
          access_token: newToken
        };
      } catch {
        refreshFailureHandler();
        return null;
      }
    };
    $[12] = refreshFailureHandler;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      refreshInterval() {
        const accessToken_0 = getBearerToken();
        if (!accessToken_0) {
          return 30000;
        }
        const seconds = getSecondsUntilExpiry(accessToken_0);
        return Math.max(0, (seconds - REFRESH_THRESHOLD_SECONDS) * 1000);
      },
      revalidateOnFocus: true,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      shouldRetryOnError: false
    };
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  useSWR(status === "authenticated" ? "auth/token-refresh" : null, t12, t13);
  let t14;
  if ($[15] !== isSending || $[16] !== resetVerifyOtp || $[17] !== triggerInitiateLogin) {
    t14 = async email_0 => {
      if (isSending) {
        return;
      }
      resetVerifyOtp();
      await triggerInitiateLogin(email_0);
    };
    $[15] = isSending;
    $[16] = resetVerifyOtp;
    $[17] = triggerInitiateLogin;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  const initiateLoginHandler = t14;
  let t15;
  if ($[19] !== isVerifying || $[20] !== triggerVerifyOtp) {
    t15 = async (email_1, otp) => {
      if (isVerifying) {
        return;
      }
      await triggerVerifyOtp({
        email: email_1,
        otp
      });
    };
    $[19] = isVerifying;
    $[20] = triggerVerifyOtp;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  const verifyOtpHandler = t15;
  let t16;
  if ($[22] !== triggerLogout) {
    t16 = async () => {
      await triggerLogout();
    };
    $[22] = triggerLogout;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  const logoutHandler = t16;
  let t17;
  if ($[24] !== resetInitiateLogin || $[25] !== resetLogout || $[26] !== resetVerifyOtp) {
    t17 = () => {
      resetInitiateLogin();
      resetVerifyOtp();
      resetLogout();
    };
    $[24] = resetInitiateLogin;
    $[25] = resetLogout;
    $[26] = resetVerifyOtp;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  const clearError = t17;
  let t18;
  if ($[28] !== resetInitiateLogin || $[29] !== resetVerifyOtp) {
    t18 = () => {
      setSessionStatus("unauthenticated");
      resetInitiateLogin();
      resetVerifyOtp();
    };
    $[28] = resetInitiateLogin;
    $[29] = resetVerifyOtp;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  const resetLoginFlow = t18;
  const isAccountComplete = status === "authenticated" && !!user?.accountComplete;
  let t19;
  if ($[31] !== clearError || $[32] !== error || $[33] !== initiateLoginHandler || $[34] !== isAccountComplete || $[35] !== logoutHandler || $[36] !== resetLoginFlow || $[37] !== sessionRenewed || $[38] !== status || $[39] !== user || $[40] !== verifyOtpHandler) {
    t19 = {
      status,
      user,
      isAccountComplete,
      error,
      initiateLogin: initiateLoginHandler,
      verifyOtp: verifyOtpHandler,
      logout: logoutHandler,
      clearError,
      resetLoginFlow,
      sessionRenewed
    };
    $[31] = clearError;
    $[32] = error;
    $[33] = initiateLoginHandler;
    $[34] = isAccountComplete;
    $[35] = logoutHandler;
    $[36] = resetLoginFlow;
    $[37] = sessionRenewed;
    $[38] = status;
    $[39] = user;
    $[40] = verifyOtpHandler;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  const value = t19;
  let t20;
  if ($[42] !== children || $[43] !== value) {
    t20 = <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
    $[42] = children;
    $[43] = value;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  return t20;
}
function _temp6() {
  return getRefreshToken();
}
function _temp5() {
  return getBearerToken();
}
function _temp4(_newTokens) {}
async function _temp3() {
  await bffLogout();
  clearTokens();
}
async function _temp2(_key_0, t0) {
  const {
    arg
  } = t0;
  const verifyResult = await verifyOtp(arg.email, arg.otp);
  setTokens(verifyResult);
  return verifyResult;
}
async function _temp(_key, t0) {
  const {
    arg: email
  } = t0;
  await initiateLogin(email);
}
export function useAuthProvider() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthProvider must be used within an AuthProvider");
  }
  return context;
}